import { AlertTriangle, DollarSign, Package, Store } from 'lucide-react';
import { type CSSProperties, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiGet } from '../api/client.js';
import { Card } from '../components/Card.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { MetricCard } from '../components/MetricCard.js';
import { PageHeader } from '../components/PageHeader.js';
import { SuggestionsCard } from '../components/SuggestionsCard.js';
import { Skeleton } from '../components/Skeleton.js';
import { useStores } from '../state/store-context.js';

interface Stats {
  activeListings: number;
  totalSales30d: number;
  underperformerCount: number;
}

interface UnderItem {
  listingId: string;
  title: string;
  priceValue: number;
  priceCurrency: string;
  listingUrl: string | null;
  primaryImageUrl: string | null;
  signals: string[];
  details: Record<string, unknown>;
}

const qaStyle: CSSProperties = {
  background: 'var(--c-card)',
  border: '1px solid var(--c-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 14,
  textAlign: 'left',
  textDecoration: 'none',
  color: 'var(--c-text)',
  fontSize: 12,
};

export function OverviewPage(): JSX.Element {
  const { stores, selectedStoreId } = useStores();
  const storeQs = selectedStoreId === 'all' ? '' : `?store_id=${selectedStoreId}`;
  const [stats, setStats] = useState<Stats | null>(null);
  const [under, setUnder] = useState<UnderItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    Promise.all([
      apiGet<Stats>(`/api/listings/stats${storeQs}`),
      apiGet<{ items: UnderItem[] }>(
        `/api/listings/underperformers${storeQs ? `${storeQs}&` : '?'}limit=5`,
      ),
    ])
      .then(([s, u]) => {
        setStats(s);
        setUnder(u.items);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? `Couldn't load stats (${e.status})` : 'Network error'),
      );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId]);

  const storeLabel =
    selectedStoreId === 'all'
      ? 'Both'
      : (stores.find((s) => s.id === selectedStoreId)?.storeName ?? '—');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title="Dashboard"
        accent="overview"
        sub={
          <>
            <span
              style={{
                display: 'inline-block',
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: 'var(--success)',
              }}
            />
            Live · {storeLabel === 'Both' ? 'both stores' : storeLabel} · auto-refresh 30s
          </>
        }
      />
      {error && <ErrorBanner message={error} onRetry={load} />}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
        }}
      >
        <MetricCard
          icon={Package}
          label="Active listings"
          value={stats?.activeListings ?? '—'}
        />
        <MetricCard
          icon={DollarSign}
          label="Total sales (30d)"
          value={stats ? `$${stats.totalSales30d.toFixed(2)} CAD` : '—'}
        />
        <MetricCard
          icon={AlertTriangle}
          label="Underperformers"
          value={stats?.underperformerCount ?? '—'}
        />
        <MetricCard icon={Store} label="Active store" value={storeLabel} />
      </div>

      <Card
        title="Remove these"
        description="Top 5 listings flagged as underperforming"
      >
        {under === null ? (
          <Skeleton height={120} />
        ) : under.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--c-muted)' }}>
            No underperforming listings — good shape.
          </p>
        ) : (
          under.map((u) => (
            <div
              key={u.listingId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 0',
                borderBottom: '1px solid var(--c-border)',
              }}
            >
              {u.primaryImageUrl && (
                <img
                  src={u.primaryImageUrl}
                  alt=""
                  width={32}
                  height={32}
                  style={{ borderRadius: 8, objectFit: 'cover' }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{u.title}</div>
                <div style={{ fontSize: 11, color: 'var(--c-muted)' }}>
                  ${u.priceValue.toFixed(2)} {u.priceCurrency} ·{' '}
                  {u.signals.join(', ').replaceAll('_', ' ')}
                </div>
              </div>
              {u.listingUrl && (
                <a href={u.listingUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                  View →
                </a>
              )}
            </div>
          ))
        )}
      </Card>

      <SuggestionsCard storeName={storeLabel} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
        }}
      >
        <Link to="/research" style={qaStyle}>
          🔍 Research a product
        </Link>
        <Link to="/vendors" style={qaStyle}>
          ⚖️ Compare vendors
        </Link>
        <Link to="/price-calc" style={qaStyle}>
          🧮 Price calculator
        </Link>
        <Link to="/ai-tools" style={qaStyle}>
          ✨ Generate listing
        </Link>
      </div>
    </div>
  );
}
